import { AdScripts } from "@components/ads";
import LayoutProvider from "@components/LayoutProvider";
import ReactQueryProvider from "@components/ReactQueryProvider";
import { AnalyticsScripts } from "@hooks/useAnalytics";
import { ClientContextProvider } from "@hooks/useClientContext";
import Cookies from "cookie";
import NextApp, { AppContext, AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { publicBasePath } from "src/styles/styleUtils";
import settings, { ENV_NAMES } from "../settings";
import AppGlobalCSS from "../styles/global.styles";
import NormalizeGlobalCSS from "../styles/normalize.styles";
import TypographyGlobalCSS from "../styles/typography.styles";
const { APP_ENV, GAME_UID, API_BACKEND_HOSTNAME, COOKIE_NAMES } = settings;
const { nativeTokenCookieName } = COOKIE_NAMES;

function App({ Component, pageProps, err }: AppProps & { err: Error }): JSX.Element {
  return (
    <>
      <NormalizeGlobalCSS />
      <TypographyGlobalCSS />
      <AppGlobalCSS />
      <Head>
        <title key={`${GAME_UID}-title`}>NASCAR Playoffs Grid Challenge</title>
        <link
          rel="shortcut icon"
          id={`${GAME_UID}-favicon`}
          key={`${GAME_UID}-favicon`}
          href={`${publicBasePath}/favicon${APP_ENV === ENV_NAMES.DEV ? "-dark" : ""}.ico`}
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
          key="viewport"
        />
        <meta content="website" property="og:type" key="og:type" />
        <meta content="NASCAR Playoffs Grid Challenge" property="og:site_name" key="og:site_name" />
        {/* <meta content={`${API_BACKEND_HOSTNAME}${publicBasePath}`} property="og:url" key="og:url" /> */}
        <meta content="Play NASCAR Playoffs Grid Challenge!" property="og:title" key="og:title" />
        <meta
          content="I’m playing The #NASCAR Playoffs Grid Challenge. Join a league and play now for a chance to win $10,000!"
          property="og:description"
          key="og:description"
        />
        <meta
          content={`${API_BACKEND_HOSTNAME}${publicBasePath}/images/share.png`}
          property="og:image"
          key="og:image"
        />
      </Head>
      <ClientContextProvider>
        <ReactQueryProvider pageProps={pageProps}>
          <LayoutProvider>
            <Component {...pageProps} err={err} />
          </LayoutProvider>
        </ReactQueryProvider>
      </ClientContextProvider>
      <Script
        id={`${GAME_UID}-gigya`}
        key={`${GAME_UID}-gigya`}
        type="text/javascript"
        src={`https://cdns.gigya.com/JS/socialize.js?apikey=${settings.GIGYA_API_KEY}&version=latest`}
        strategy="beforeInteractive"
      />
      <AdScripts />
      <AnalyticsScripts />
    </>
  );
}

/**
 * We use getInitialProps to capture global query params before landing on page routes & before rendering the DOM.
 * This is to avoid flashing and possible layout shifts based on the experience type, in contrast to using `query` from next/router.
 *
 * Adding getInitialProps at the App component level opts us out of SSG (unless getStaticProps is explicitly used on child pages).
 * The app is now SSR/CSR by default 🔥 (Server Side Rendered or Client-Side Rendered)
 *
 * Example— to test query param detection, visit: https://sti.nascar.com:3002?iframe
 */
App.getInitialProps = async (appContext: AppContext) => {
  const isNative = appContext.router.query.native !== undefined;

  // call Page's `getInitialProps` and add to `appProps.pageProps`
  const appProps = await NextApp.getInitialProps(appContext);

  const req = appContext.ctx.req;
  let nativeToken = "";

  if (req?.headers?.cookie) {
    const cookies = Cookies.parse(req.headers.cookie);
    nativeToken = cookies[nativeTokenCookieName] ?? "";
  }

  if (appProps?.pageProps) {
    appProps.pageProps.isNative = isNative || nativeToken;
    appProps.pageProps.nativeToken = nativeToken;
  }

  return { ...appProps };
};

export default App;
